// src/hooks/usePricingPresentation.js
import { useEffect, useMemo, useState } from "react";

/**
 * FRONTEND PRICING PRESENTATION LOADER
 *
 * PURPOSE
 * -------
 * This hook gives the React app a safe, runtime-friendly way to consume the
 * same merged pricing presentation payload that WordPress already uses.
 *
 * IMPORTANT
 * ---------
 * We intentionally DO NOT read the admin Firestore pricing presentation doc
 * directly from the subscription page.
 *
 * Why:
 * - The Firestore admin document is part of the controlled editing workflow.
 * - Its read/write helpers are intentionally role-gated.
 * - The app pricing page should consume the already-generated public payload,
 *   not the raw admin document shape.
 *
 * SOURCE
 * ------
 * This hook reads the generated pricing JSON that is already produced by:
 *   scripts/generate-pricing-json.js
 *
 * That means the app now consumes the same merged output as WordPress:
 * - billing/source-of-truth plan data
 * - pricing presentation content
 * - compare categories/rows
 * - CTA semantics
 * - pricingDisplay rules
 *
 * DESIGN GOALS
 * ------------
 * 1. Do NOT duplicate pricing merge logic in the app.
 * 2. Keep billing logic and rendering logic separate.
 * 3. Add clear debug logs so local testing is easy.
 * 4. Fail gracefully by returning empty/default structures.
 * 5. Allow future extension if you later want environment-specific payloads.
 */

function resolvePricingPayloadPath() {
  /**
   * Resolution order:
   * 1) explicit override via REACT_APP_PRICING_PAYLOAD_PATH
   * 2) billing env hint via REACT_APP_BILLING_ENV
   * 3) default production payload
   */
  const explicitPath = process.env.REACT_APP_PRICING_PAYLOAD_PATH;
  if (explicitPath && typeof explicitPath === "string") {
    return explicitPath.trim();
  }

  const billingEnv = (
    process.env.REACT_APP_BILLING_ENV || "prod"
  ).toString().trim().toLowerCase();

  if (billingEnv === "staging") {
    return "/pricing-data.staging.json";
  }

  return "/pricing-data.json";
}

const EMPTY_PRICING_PRESENTATION = {
  loading: true,
  error: null,
  payloadPath: resolvePricingPayloadPath(),
  version: null,
  generatedAt: null,
  source: null,
  target: null,
  pricingEnv: null,
  defaults: {
    interval: "annual",
    seats: 10,
    appBaseUrl: "",
    contactSalesUrl: "",
  },
  savePct: null,
  plans: [],
  compare: [],
  meta: {
    presentationSource: null,
    usedPresentationFallback: false,
    firestoreProjectId: null,
    warning: null,
  },
};

export default function usePricingPresentation() {
  const [state, setState] = useState(EMPTY_PRICING_PRESENTATION);

  useEffect(() => {
    let isCancelled = false;
    const payloadPath = resolvePricingPayloadPath();

    async function loadPricingPresentation() {
      console.log("[usePricingPresentation] Starting pricing payload load:", {
        payloadPath,
      });

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        payloadPath,
      }));

      try {
        const response = await fetch(payloadPath, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(
            `Failed to load pricing payload. HTTP ${response.status}`
          );
        }

        const json = await response.json();

        const normalized = {
          loading: false,
          error: null,
          payloadPath,
          version: json?.version ?? null,
          generatedAt: json?.generatedAt ?? null,
          source: json?.source ?? null,
          target: json?.target ?? null,
          pricingEnv: json?.pricingEnv ?? null,
          defaults: {
            interval: json?.defaults?.interval ?? "annual",
            seats: json?.defaults?.seats ?? 10,
            appBaseUrl: json?.defaults?.appBaseUrl ?? "",
            contactSalesUrl: json?.defaults?.contactSalesUrl ?? "",
          },
          savePct: json?.savePct ?? null,
          plans: Array.isArray(json?.plans) ? json.plans : [],
          compare: Array.isArray(json?.compare) ? json.compare : [],
          meta: {
            presentationSource: json?._meta?.presentationSource ?? null,
            usedPresentationFallback:
              json?._meta?.usedPresentationFallback === true,
            firestoreProjectId: json?._meta?.firestoreProjectId ?? null,
            warning: json?._meta?.warning ?? null,
          },
        };

        if (isCancelled) return;

        console.log("[usePricingPresentation] Pricing payload loaded:", {
          payloadPath,
          version: normalized.version,
          target: normalized.target,
          pricingEnv: normalized.pricingEnv,
          planKeys: normalized.plans.map((plan) => plan.key),
          compareCategories: normalized.compare.map((group) => group.category),
          savePct: normalized.savePct,
          presentationSource: normalized.meta.presentationSource,
          usedPresentationFallback: normalized.meta.usedPresentationFallback,
          warning: normalized.meta.warning,
        });

        setState(normalized);
      } catch (error) {
        if (isCancelled) return;

        console.error("[usePricingPresentation] Failed to load pricing payload:", {
          payloadPath,
          message: error?.message || error,
        });

        setState({
          ...EMPTY_PRICING_PRESENTATION,
          loading: false,
          error: error?.message || "Failed to load pricing payload.",
          payloadPath,
        });
      }
    }

    loadPricingPresentation();

    return () => {
      isCancelled = true;
    };
  }, []);

  return useMemo(() => state, [state]);
}